<template>
  <div class="global-user-drawer-root">
    <transition name="user-drawer-mask">
      <div
        v-if="isOpen"
        class="user-drawer-mask"
        aria-hidden="true"
        @click="handleMaskClick"
      ></div>
    </transition>

    <transition name="user-drawer-panel">
      <div
        v-if="isOpen"
        class="user-drawer-surface"
        :style="drawerStyle"
      >
        <button
          class="user-drawer-close"
          type="button"
          aria-label="关闭个人中心"
          @click="closeDrawer"
        >&times;</button>

        <aside
          ref="drawerPanel"
          class="user-drawer-panel"
          role="dialog"
          aria-modal="true"
          aria-label="个人中心"
          @click.stop
        >
          <keep-alive>
            <component
              :is="currentComponent"
              v-bind="currentProps"
              @open-invoice="handleOpenInvoice"
              @back-to-overview="handleBackToOverview"
              @navigate-section="handleNavigateSection"
            ></component>
          </keep-alive>
        </aside>
      </div>
    </transition>
  </div>
</template>

<script>
import userCenter from '@/store/userCenter'
import UserCenterView from '@/views/UserCenterView.vue'
import InvoiceApplyView from '@/views/InvoiceApplyView.vue'

// 常量：抽屉宽度与各业务视图的现有内容画布宽度保持一致
const DRAWER_WIDTHS = {
  overview: 791,
  invoice: 999
}

export default {
  name: 'GlobalUserDrawer',
  components: { UserCenterView, InvoiceApplyView },
  data() {
    return {
      userCenterState: userCenter.state,
      previousBodyOverflow: '',
      bodyScrollLocked: false
    }
  },
  computed: {
    // 方法：根据全局视图状态选择抽屉中的业务页面
    currentComponent() {
      return this.userCenterState.view === 'invoice'
        ? 'InvoiceApplyView'
        : 'UserCenterView'
    },
    // 方法：按当前视图传递对应参数，避免动态组件接收无关属性
    currentProps() {
      return this.userCenterState.view === 'invoice'
        ? { initialOrderNo: this.userCenterState.orderNo }
        : { initialSection: this.userCenterState.section }
    },
    // 方法：根据当前展示内容切换抽屉宽度，并限制不超出浏览器视口
    drawerStyle() {
      const width = DRAWER_WIDTHS[this.userCenterState.view] || DRAWER_WIDTHS.overview
      return {
        width: `${width}px`,
        maxWidth: '100vw'
      }
    },
    // 方法：统一读取抽屉是否打开，供模板和键盘逻辑复用
    isOpen() {
      return this.userCenterState.visible
    }
  },
  watch: {
    // 关键节点：抽屉打开时锁定背景页面滚动，关闭时恢复原始样式
    isOpen(visible) {
      this.syncBodyScroll(visible)
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown)
    this.syncBodyScroll(false)
  },
  methods: {
    // 方法：同步背景页面滚动锁定状态
    syncBodyScroll(locked) {
      if (locked && !this.bodyScrollLocked) {
        this.previousBodyOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        this.bodyScrollLocked = true
        return
      }

      // 逻辑分支：只有此前确实锁定过滚动时才恢复，避免覆盖外部样式
      if (!locked && this.bodyScrollLocked) {
        document.body.style.overflow = this.previousBodyOverflow
        this.previousBodyOverflow = ''
        this.bodyScrollLocked = false
      }
    },
    // 方法：关闭全局个人中心抽屉
    closeDrawer() {
      userCenter.close()
    },
    // 方法：遮罩区域点击关闭，抽屉内容点击由 stop 修饰符拦截
    handleMaskClick() {
      this.closeDrawer()
    },
    // 方法：按 Escape 关闭抽屉
    handleKeydown(event) {
      if (this.isOpen && event.key === 'Escape') {
        this.closeDrawer()
      }
    },
    // 方法：从概览切换到申请发票视图
    handleOpenInvoice(payload) {
      const orderNo = payload && payload.orderNo
      userCenter.openInvoice(orderNo)
      this.scrollDrawerToTop()
    },
    // 方法：从申请发票页返回概览并定位发票模块
    handleBackToOverview(section = 'invoice') {
      userCenter.openOverview(section)
      this.scrollDrawerToTop()
    },
    // 方法：发票页左侧菜单切换到概览模块
    handleNavigateSection(section) {
      userCenter.openOverview(section)
      this.scrollDrawerToTop()
    },
    // 方法：切换抽屉视图后将外层滚动容器复位到顶部
    scrollDrawerToTop() {
      this.$nextTick(() => {
        const panel = this.$refs.drawerPanel
        if (panel) {
          panel.scrollTop = 0
        }
      })
    }
  }
}
</script>

<style scoped lang="less">
.global-user-drawer-root,
.user-drawer-mask,
.user-drawer-surface{
  position: fixed;
  inset: 0;
}

.global-user-drawer-root{
  z-index: 9999;
  pointer-events: none;
}

.user-drawer-mask{
  z-index: 0;
  background: rgba(0, 0, 0, 0.7);
  pointer-events: auto;
}

.user-drawer-surface{
  z-index: 1;
  height: 100vh;
  background: #F7FAFF;
  box-shadow: 12px 0 32px rgba(0, 0, 0, 0.16);
  pointer-events: auto;
}

.user-drawer-panel{
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
}

.user-drawer-close{
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  color: #666;
  font-size: 20px;
  line-height: 20px;
  cursor: pointer;
}

.user-drawer-mask-enter-active,
.user-drawer-mask-leave-active{
  transition: opacity 0.2s ease;
}

.user-drawer-mask-enter,
.user-drawer-mask-leave-to{
  opacity: 0;
}

.user-drawer-panel-enter-active,
.user-drawer-panel-leave-active{
  transition: transform 0.24s ease;
}

.user-drawer-panel-enter,
.user-drawer-panel-leave-to{
  transform: translateX(-100%);
}
</style>
